import type { ResultsRuntimeSnapshot } from "./results-runtime";
import {
  hapticFeedbackConfig,
  hapticSignalFor,
  type HapticFeedbackEvent,
  type HapticFeedbackSignal,
} from "../config/haptic-feedback";
import { recordBoundedIdentifier } from "./runtime-stability";
import { gameplayConfig } from "../config/gameplay-config";

export interface HapticBoundary {
  isSupported(): boolean;
  play(signal: HapticFeedbackSignal): Promise<void>;
}

export interface HapticBonusSource {
  getSnapshot(): {
    readonly lastEvent: {
      readonly bonusId: string;
      readonly revision: number;
      readonly type: "turbo";
    } | null;
    readonly turboActiveRemainingMs: number;
  };
  subscribe(listener: () => void): () => void;
}

export interface HapticBotDefeatSource {
  subscribe(
    listener: (outcome: {
      readonly contactId: string;
      readonly runId: number;
      readonly winnerId: string;
    }) => void,
  ): () => void;
}

export interface HapticPlayerDefeatSource {
  getSnapshot(): {
    readonly respawnAtMs: number | null;
    readonly state: string;
  };
  subscribe(listener: () => void): () => void;
}

export interface HapticPlayerSource {
  subscribe(
    listener: (snapshot: {
      readonly feedback: { readonly revision: number } | null;
      readonly runId: number;
    }) => void,
  ): () => void;
}

export interface HapticResultsSource {
  subscribe(listener: (snapshot: ResultsRuntimeSnapshot) => void): () => void;
}

export interface HapticPreferenceSource {
  getState(): { readonly preferences: { readonly hapticsEnabled: boolean } };
}

export interface HapticRuntimeOptions {
  readonly bonusRuntime: HapticBonusSource;
  readonly botDefeatRuntime: HapticBotDefeatSource;
  readonly boundary: HapticBoundary;
  readonly now: () => number;
  readonly preferenceSource: HapticPreferenceSource;
  readonly playerDefeatRuntime: HapticPlayerDefeatSource;
  readonly playerRuntime: HapticPlayerSource;
  readonly resultsRuntime: HapticResultsSource;
}

export class HapticRuntime {
  private destroyed = false;
  private readonly lastFeedbackAt = new Map<HapticFeedbackEvent, number>();
  private operation = Promise.resolve();
  private previousDefeatKey: string | null = null;
  private previousTurboPickupKey: string | null = null;
  private readonly processedEventIds = new Set<string>();
  private turboActive = false;
  private readonly unsubscribers: (() => void)[] = [];

  public constructor(private readonly options: HapticRuntimeOptions) {
    this.unsubscribers.push(
      options.bonusRuntime.subscribe(() => {
        this.syncTurbo();
      }),
      options.botDefeatRuntime.subscribe((outcome) => {
        this.syncBotDefeat(outcome);
      }),
      options.playerDefeatRuntime.subscribe(() => {
        this.syncPlayerDefeat();
      }),
      options.playerRuntime.subscribe((snapshot) => {
        this.syncPlayer(snapshot);
      }),
      options.resultsRuntime.subscribe((snapshot) => {
        this.syncResult(snapshot);
      }),
    );
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers.length = 0;
  }

  private play(event: HapticFeedbackEvent): void {
    if (
      !this.options.preferenceSource.getState().preferences.hapticsEnabled ||
      !this.options.boundary.isSupported()
    ) {
      return;
    }
    const occurredAt = this.options.now();
    const previous = this.lastFeedbackAt.get(event);
    if (
      previous !== undefined &&
      occurredAt - previous < hapticFeedbackConfig.cooldownMs[event]
    ) {
      return;
    }
    this.lastFeedbackAt.set(event, occurredAt);
    const signal = hapticSignalFor(event);
    this.operation = this.operation.then(async () => {
      try {
        await this.options.boundary.play(signal);
      } catch {
        return;
      }
    });
  }

  private recordEvent(identifier: string): boolean {
    if (this.processedEventIds.has(identifier)) {
      return false;
    }
    recordBoundedIdentifier(
      this.processedEventIds,
      identifier,
      gameplayConfig.runtimeStability.maximumProcessedEvents,
    );
    return true;
  }

  private syncBotDefeat(outcome: {
    readonly contactId: string;
    readonly runId: number;
    readonly winnerId: string;
  }): void {
    const identifier = `${String(outcome.runId)}:snake-eaten:${outcome.contactId}`;
    if (outcome.winnerId === "player" && this.recordEvent(identifier)) {
      this.play("snake-eaten");
    }
  }

  private syncPlayerDefeat(): void {
    const snapshot = this.options.playerDefeatRuntime.getSnapshot();
    const defeatKey =
      snapshot.state === "waiting" && snapshot.respawnAtMs !== null
        ? String(snapshot.respawnAtMs)
        : null;
    if (defeatKey !== null && defeatKey !== this.previousDefeatKey) {
      this.play("player-defeat");
    }
    this.previousDefeatKey = defeatKey;
  }

  private syncPlayer(snapshot: {
    readonly feedback: { readonly revision: number } | null;
    readonly runId: number;
  }): void {
    const feedback = snapshot.feedback;
    if (feedback === null) {
      return;
    }
    const identifier = `${String(snapshot.runId)}:food-pickup:${String(feedback.revision)}`;
    if (this.recordEvent(identifier)) {
      this.play("food-pickup");
    }
  }

  private syncResult(snapshot: ResultsRuntimeSnapshot): void {
    const result = snapshot.result;
    if (
      !snapshot.navigationAvailable ||
      result === null ||
      !this.recordEvent(`result:${result.id}`)
    ) {
      return;
    }
    this.play(result.isNewBestLevel ? "personal-best" : "challenge-complete");
  }

  private syncTurbo(): void {
    const snapshot = this.options.bonusRuntime.getSnapshot();
    const pickup = snapshot.lastEvent;
    const pickupKey =
      pickup === null ? null : `${pickup.bonusId}:${String(pickup.revision)}`;
    if (pickupKey !== null && pickupKey !== this.previousTurboPickupKey) {
      this.play("turbo");
    }
    this.previousTurboPickupKey = pickupKey;
    const active = snapshot.turboActiveRemainingMs > 0;
    if (active && !this.turboActive) {
      this.play("turbo");
    }
    this.turboActive = active;
  }
}
